import React, { useEffect, useState } from "react";
import { SpotCat } from "@/hooks/useSpotCategories";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Trees } from "lucide-react";
import { PageTitle } from "@/components/SettingsPanel";
import { SpotForm } from "@/components/SpotForm";
import { AdminSpotReviews } from "@/components/AdminSpotReviews";
import { AdminSpotSuggestions } from "@/components/AdminSpotSuggestions";
import { api, errorText } from "@/lib/api";
import { imgSrc } from "@/lib/img";
import type { Spot } from "@/lib/spots";
import { usePrefs } from "@/contexts/PrefsContext";
import { useAuth } from "@/contexts/AuthContext";

export default function AdminSpots() {
  const { t } = usePrefs();
  const { user } = useAuth();
  const canEdit = !!user && (user.admin_role || "super") === "super";
  const [rows, setRows] = useState<Spot[] | null>(null);
  const [edit, setEdit] = useState<Spot | "new" | null>(null);
  const [tab, setTab] = useState<"spots" | "reviews" | "suggestions">("spots");
  const load = () => api.get("/admin/public-spots").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const del = async (s: Spot) => {
    if (!window.confirm(t("spot.confirmDelete").replace("{name}", s.name_ar))) return;
    try { await api.delete(`/admin/public-spots/${s.spot_id}`); toast.success(t("spot.deleted")); load(); } catch (e) { toast.error(errorText(e, t)); }
  };

  return (
    <div data-testid="admin-spots-page">
      <PageTitle title={t("spot.adminTitle")} sub={t("spot.adminSub")} action={canEdit && (
        <button data-testid="spot-add-button" onClick={() => setEdit("new")} className="h-11 px-5 rounded-full bg-primary text-primary-foreground font-bold inline-flex items-center gap-1.5"><Plus className="h-4 w-4" />{t("spot.add")}</button>
      )} />
      <div className="flex gap-2 mb-5 overflow-x-auto no-scrollbar">
        {(["spots", "suggestions", "reviews"] as const).map((k) => (
          <button key={k} data-testid={`admin-spots-tab-${k}`} aria-pressed={tab === k} onClick={() => setTab(k)}
            className={`h-10 px-5 shrink-0 whitespace-nowrap rounded-full text-sm font-bold ${tab === k ? "bg-primary text-primary-foreground" : "bg-card border"}`}>{t(k === "spots" ? "spot.tabSpots" : k === "reviews" ? "spot.tabReviews" : "sug.tab")}</button>
        ))}
      </div>
      {tab === "reviews" ? <AdminSpotReviews canEdit={canEdit} /> : tab === "suggestions" ? <AdminSpotSuggestions canEdit={canEdit} onApproved={load} /> : <>
      {rows?.length === 0 && <p data-testid="admin-spots-empty" className="text-center text-muted-foreground py-16">{t("spot.adminEmpty")}</p>}
      <div className="grid gap-3">
        {rows?.map((s) => (
          <div key={s.spot_id} data-testid={`admin-spot-row-${s.spot_id}`} className="flex items-center gap-3 rounded-3xl border bg-card p-3">
            <div className="h-16 w-20 shrink-0 overflow-hidden rounded-2xl bg-emerald-100 grid place-items-center">
              {s.gallery[0] ? <img src={imgSrc(s.gallery[0])} alt="" className="h-full w-full object-cover" /> : <Trees className="h-6 w-6 text-emerald-600" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold truncate">{s.name_ar}</p>
              <p className="text-xs text-muted-foreground"><SpotCat slug={s.category} /> · {t(`gov.${s.governorate}`)}{!s.active && <span className="ms-2 rounded-full bg-muted px-2 py-0.5 font-bold" data-testid={`admin-spot-hidden-${s.spot_id}`}>{t("spot.hidden")}</span>}</p>
            </div>
            {canEdit && <>
              <button data-testid={`admin-spot-edit-${s.spot_id}`} onClick={() => setEdit(s)} className="h-10 w-10 grid place-items-center rounded-full bg-accent"><Pencil className="h-4 w-4" /></button>
              <button data-testid={`admin-spot-delete-${s.spot_id}`} onClick={() => del(s)} className="h-10 w-10 grid place-items-center rounded-full bg-rose-50 text-rose-600"><Trash2 className="h-4 w-4" /></button>
            </>}
          </div>
        ))}
      </div>
      </>}
      {edit && <SpotForm spot={edit === "new" ? null : edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}
